import React, { useMemo, useState } from 'react';
import {
    ArrowRight,
    CaretDown,
    CaretUp,
    Check,
    Clock,
    ClockCounterClockwise,
    Lightbulb,
    ListBullets,
    Question,
    WarningCircle,
} from '@phosphor-icons/react';
import type { SalesRepChecklistEngine, FlaggedItem } from './salesrepChecklistEngine';
import type { ProcessedAuditTrailData } from './salesrepAuditTrail';

type SalesRepChecklistSummaryProps = {
    engine: SalesRepChecklistEngine;
    tick: number;
    auditTrail: ProcessedAuditTrailData;
    onNavigateToQuestion: (item: FlaggedItem) => void;
};

export const SalesRepChecklistSummary: React.FC<SalesRepChecklistSummaryProps> = ({
    engine,
    tick,
    auditTrail,
    onNavigateToQuestion,
}) => {
    const [smartInsightsExpanded, setSmartInsightsExpanded] = useState(true);
    const [sectionBreakdownExpanded, setSectionBreakdownExpanded] = useState(true);
    const [auditTrailExpanded, setAuditTrailExpanded] = useState(true);
    const [auditFilter, setAuditFilter] = useState<'all' | 'changes'>('all');

    const { smartInsights, flaggedItems, sectionBreakdownGroups, totalSectionsCount } = useMemo(() => {
        let flagged = 0;
        let needsReview = 0;
        let unanswered = 0;
        let criticalFlags = 0;
        let nonCriticalFlags = 0;
        const flaggedList: FlaggedItem[] = [];
        const processedQuestionIds = new Set<any>();
        const breakdownGroups: {
            checklistName: string;
            sections: {
                sectionId: any;
                sectionName: string;
                isCritical: boolean;
                isNonCritical: boolean;
                answeredCount: number;
                totalCount: number;
                criticalCount: number;
                reviewCount: number;
                status: 'complete' | 'in-progress' | 'not-started' | 'flagged';
            }[];
        }[] = [];
        let totalSections = 0;

        (engine.salesrepChecklistDetails || []).forEach((checklist: any) => {
            const checklistName = checklist.checklistName;
            const templates = engine.groupSectionsByProduct(checklistName);
            const tabs =
                checklistName === 'Contract Submission'
                    ? engine.getChecklistSubTabs(checklistName, templates)
                    : engine.getActiveProducts();
            const sectionMap = new Map<string, any>();
            const mappedSections: any[] = [];

            tabs.forEach((tabKey: string) => {
                (templates[tabKey] || []).forEach((sec: any) => {
                    if (!sec || sec.sectionId === 0) return;
                    const questions = engine.getAllActiveQuestionsForSection(sec, tabKey);
                    let answeredCount = 0;
                    let criticalCount = 0;
                    let reviewCount = 0;
                    questions.forEach((q: any) => {
                        const qId = q.questionId !== undefined && q.questionId !== null ? q.questionId : q;
                        const uniqueKey = `${checklistName}_${tabKey}_${sec.sectionId || sec.sectionName}_${qId}`;
                        if (!processedQuestionIds.has(uniqueKey)) {
                            processedQuestionIds.add(uniqueKey);
                            if (!engine.isQuestionAnswered(q)) unanswered++;
                            const tone = engine.getQuestionSelectedTone(q);
                            const toneColor = engine.getQuestionSelectedToneColor(q);
                            if (tone === 'red') {
                                flagged++;
                                criticalFlags++;
                                flaggedList.push({
                                    questionId: q.questionId,
                                    displayOrder: q.displayOrder || '',
                                    questionText: q.text || q.questionText || 'Question',
                                    answerText: engine.getQuestionAnswerText(q),
                                    checklistName,
                                    productTab: tabKey,
                                    sectionId: sec.sectionId,
                                    color: toneColor || '#dc2626',
                                    tone: 'red',
                                });
                            } else if (tone === 'amber') {
                                needsReview++;
                                nonCriticalFlags++;
                                flaggedList.push({
                                    questionId: q.questionId,
                                    displayOrder: q.displayOrder || '',
                                    questionText: q.text || q.questionText || 'Question',
                                    answerText: engine.getQuestionAnswerText(q),
                                    checklistName,
                                    productTab: tabKey,
                                    sectionId: sec.sectionId,
                                    color: toneColor || '#d97706',
                                    tone: 'amber',
                                });
                            }
                        }
                        if (engine.isQuestionAnswered(q)) answeredCount++;
                        const tone = engine.getQuestionSelectedTone(q);
                        if (tone === 'red') criticalCount++;
                        else if (tone === 'amber') reviewCount++;
                    });

                    const totalCount = questions.length;
                    const secName = sec.sectionName || sec.title || 'Section';
                    const secKey = String(sec.sectionName || sec.title || sec.sectionId).trim().toLowerCase();
                    if (sectionMap.has(secKey)) {
                        const existing = sectionMap.get(secKey);
                        existing.answeredCount += answeredCount;
                        existing.totalCount += totalCount;
                        existing.criticalCount += criticalCount;
                        existing.reviewCount += reviewCount;
                        existing.isCritical = existing.criticalCount > 0;
                        existing.isNonCritical = existing.criticalCount === 0 && existing.reviewCount > 0;
                        if (existing.criticalCount > 0 || existing.reviewCount > 0) existing.status = 'flagged';
                        else if (existing.totalCount > 0 && existing.answeredCount === existing.totalCount)
                            existing.status = 'complete';
                        else if (existing.answeredCount > 0) existing.status = 'in-progress';
                        else existing.status = 'not-started';
                    } else {
                        let status: 'complete' | 'in-progress' | 'not-started' | 'flagged' = 'not-started';
                        if (criticalCount > 0 || reviewCount > 0) status = 'flagged';
                        else if (totalCount > 0 && answeredCount === totalCount) status = 'complete';
                        else if (answeredCount > 0) status = 'in-progress';
                        const newSection = {
                            sectionId: sec.sectionId,
                            sectionName: secName,
                            answeredCount,
                            totalCount,
                            criticalCount,
                            reviewCount,
                            status,
                            isCritical: criticalCount > 0,
                            isNonCritical: criticalCount === 0 && reviewCount > 0,
                        };
                        sectionMap.set(secKey, newSection);
                        mappedSections.push(newSection);
                    }
                });
            });

            if (mappedSections.length > 0) {
                totalSections += mappedSections.length;
                breakdownGroups.push({ checklistName, sections: mappedSections });
            }
        });

        return {
            smartInsights: {
                flagged,
                needsReview,
                unanswered,
                criticalFlags,
                nonCriticalFlags,
                totalRemaining: unanswered,
                allGood: flagged === 0 && needsReview === 0,
            },
            flaggedItems: flaggedList,
            sectionBreakdownGroups: breakdownGroups,
            totalSectionsCount: totalSections,
        };
    }, [engine, tick]);

    const flaggedColor = flaggedItems.find((item) => item.tone === 'red')?.color || '#dc2626';
    const amberColor = flaggedItems.find((item) => item.tone === 'amber')?.color || '#d97706';
    const hasAuditTrailData = (auditTrail?.groups?.length || 0) > 0 || (auditTrail?.totalEntriesCount || 0) > 0;

    const filteredAuditGroups = useMemo(() => {
        const rawGroups = auditTrail?.groups || [];
        let groups = rawGroups;
        if (auditFilter === 'changes') {
            groups = rawGroups
                .map((group) => ({
                    ...group,
                    history: group.history.filter(
                        (h) => h.previousValue && h.previousValue !== 'First entry' && h.previousValue !== h.newValue,
                    ),
                }))
                .filter((group) => group.history.length > 0);
        }
        return [...groups].sort((a, b) => {
            const timeA = a.history.length > 0 ? a.history[0].dateObj?.getTime() || 0 : 0;
            const timeB = b.history.length > 0 ? b.history[0].dateObj?.getTime() || 0 : 0;
            return timeB - timeA;
        });
    }, [auditTrail, auditFilter]);

    return (
        <div className="obd-src-summary">
            <div className="obd-src-summary-card">
                <button type="button" className="obd-src-summary-card__head" onClick={() => setSmartInsightsExpanded((v) => !v)}>
                    <span className="obd-src-summary-card__title">
                        <Lightbulb size={18} weight="duotone" />
                        Smart insights
                    </span>
                    <span className="obd-src-summary-card__meta">
                        {smartInsights.flagged > 0 && (
                            <span style={{ color: flaggedColor }}>
                                {smartInsights.flagged} item{smartInsights.flagged > 1 ? 's' : ''} need attention
                            </span>
                        )}
                        {smartInsights.flagged === 0 && smartInsights.needsReview > 0 && (
                            <span style={{ color: amberColor }}>
                                {smartInsights.needsReview} item{smartInsights.needsReview > 1 ? 's' : ''} to review
                            </span>
                        )}
                        {smartInsights.flagged === 0 && smartInsights.needsReview === 0 && smartInsights.allGood && (
                            <span className="obd-src-pill obd-src-pill--good">All good</span>
                        )}
                        {smartInsights.flagged === 0 && smartInsights.needsReview > 0 && (
                            <span className="obd-src-pill obd-src-pill--review">Needs review</span>
                        )}
                        {smartInsightsExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                    </span>
                </button>
                {smartInsightsExpanded && (
                    <div className="obd-src-summary-card__body">
                        <div className="obd-src-insight-grid">
                            <div
                                className={`obd-src-insight-box ${smartInsights.flagged > 0 ? 'is-red' : 'is-green'}`}
                                style={
                                    smartInsights.flagged > 0
                                        ? { borderColor: flaggedColor, backgroundColor: `${flaggedColor}10` }
                                        : undefined
                                }
                            >
                                <div className="obd-src-insight-box__num" style={smartInsights.flagged > 0 ? { color: flaggedColor } : undefined}>
                                    {smartInsights.flagged}
                                </div>
                                <div className="obd-src-insight-box__title">FLAGGED</div>
                                <div className="obd-src-insight-box__sub">
                                    {smartInsights.flagged > 0
                                        ? `${smartInsights.flagged} critical flag${smartInsights.flagged > 1 ? 's' : ''}`
                                        : 'No issues'}
                                </div>
                            </div>
                            <div
                                className={`obd-src-insight-box ${smartInsights.needsReview > 0 ? 'is-amber' : 'is-green'}`}
                                style={
                                    smartInsights.needsReview > 0
                                        ? { borderColor: amberColor, backgroundColor: `${amberColor}1A` }
                                        : undefined
                                }
                            >
                                <div className="obd-src-insight-box__num" style={smartInsights.needsReview > 0 ? { color: amberColor } : undefined}>
                                    {smartInsights.needsReview}
                                </div>
                                <div className="obd-src-insight-box__title">NEEDS REVIEW</div>
                                <div className="obd-src-insight-box__sub">
                                    {smartInsights.needsReview > 0
                                        ? `${smartInsights.needsReview} item${smartInsights.needsReview > 1 ? 's' : ''} to review`
                                        : 'Nothing to review'}
                                </div>
                            </div>
                            <div className="obd-src-insight-box is-white">
                                <div className="obd-src-insight-box__num">{smartInsights.unanswered}</div>
                                <div className="obd-src-insight-box__title">UNANSWERED</div>
                                <div className="obd-src-insight-box__sub">{smartInsights.unanswered} remaining</div>
                            </div>
                        </div>
                        <div className="obd-src-insight-grid obd-src-insight-grid--2">
                            <div
                                className="obd-src-insight-box is-gray"
                                style={
                                    smartInsights.criticalFlags > 0
                                        ? { borderColor: flaggedColor, backgroundColor: `${flaggedColor}0A` }
                                        : undefined
                                }
                            >
                                <div className="obd-src-insight-box__num" style={smartInsights.criticalFlags > 0 ? { color: flaggedColor } : undefined}>
                                    {smartInsights.criticalFlags}
                                </div>
                                <div className="obd-src-insight-box__title">CRITICAL FLAGS</div>
                                <div className="obd-src-insight-box__sub">
                                    {smartInsights.criticalFlags > 0 ? 'Resolve to avoid Track C' : 'No critical issues'}
                                </div>
                                {smartInsights.criticalFlags > 0 && (
                                    <div className="obd-src-insight-box__bar" style={{ backgroundColor: flaggedColor }} />
                                )}
                            </div>
                            <div
                                className="obd-src-insight-box is-gray"
                                style={
                                    smartInsights.nonCriticalFlags > 0
                                        ? { borderColor: amberColor, backgroundColor: `${amberColor}0A` }
                                        : undefined
                                }
                            >
                                <div className="obd-src-insight-box__num" style={smartInsights.nonCriticalFlags > 0 ? { color: amberColor } : undefined}>
                                    {smartInsights.nonCriticalFlags}
                                </div>
                                <div className="obd-src-insight-box__title">NON-CRITICAL FLAGS</div>
                                <div className="obd-src-insight-box__sub">
                                    {smartInsights.nonCriticalFlags > 0 ? 'Address to avoid Track B' : 'No non-critical issues'}
                                </div>
                                {smartInsights.nonCriticalFlags > 0 && (
                                    <div className="obd-src-insight-box__bar" style={{ backgroundColor: amberColor }} />
                                )}
                            </div>
                        </div>

                        {flaggedItems.length > 0 && (
                            <div className="obd-src-resolve">
                                <div className="obd-src-sublabel">ITEMS TO RESOLVE</div>
                                {flaggedItems.map((item, idx) => (
                                    <div
                                        key={`${item.questionId}-${idx}`}
                                        className="obd-src-flagged-item"
                                        style={{
                                            borderColor: item.color || '#dc2626',
                                            backgroundColor: `${item.color || '#dc2626'}0F`,
                                        }}
                                    >
                                        <div className="obd-src-flagged-item__body">
                                            <WarningCircle size={16} color={item.color || '#dc2626'} />
                                            <div>
                                                <div className="obd-src-flagged-item__title">
                                                    {item.displayOrder ? `Q${item.displayOrder} — ` : ''}
                                                    {item.questionText}
                                                </div>
                                                <div className="obd-src-flagged-item__sub">
                                                    Answered: &quot;{item.answerText}&quot; — Critical issue
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="obd-src-goto"
                                            style={{ ['--go-to-color' as string]: item.color }}
                                            onClick={() => onNavigateToQuestion(item)}
                                        >
                                            &gt; Go to
                                        </button>
                                    </div>
                                ))}
                                <div className="obd-src-resolve__next">
                                    <button type="button" className="obd-src-next-flagged" onClick={() => onNavigateToQuestion(flaggedItems[0])}>
                                        <ArrowRight size={14} /> Next flagged item
                                    </button>
                                </div>
                            </div>
                        )}

                        {flaggedItems.length === 0 && (
                            <div className="obd-src-insights-footer">
                                {smartInsights.totalRemaining > 0
                                    ? `${smartInsights.totalRemaining} questions remaining — keep going!`
                                    : 'All questions completed — ready for submission!'}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="obd-src-summary-card">
                <button type="button" className="obd-src-summary-card__head" onClick={() => setSectionBreakdownExpanded((v) => !v)}>
                    <span className="obd-src-summary-card__title">
                        <ListBullets size={18} weight="duotone" />
                        Section breakdown
                    </span>
                    <span className="obd-src-summary-card__meta">
                        {totalSectionsCount} sections
                        {smartInsights.flagged > 0 ? ` · ${smartInsights.flagged} flagged` : ''}
                        {smartInsights.needsReview > 0 ? ` · ${smartInsights.needsReview} to review` : ''}
                        {sectionBreakdownExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                    </span>
                </button>
                {sectionBreakdownExpanded && (
                    <div className="obd-src-summary-card__body">
                        <table className="obd-src-breakdown">
                            <thead>
                                <tr>
                                    <th>SECTION</th>
                                    <th>ANSWERED</th>
                                    <th>FLAGGED</th>
                                    <th>STATUS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sectionBreakdownGroups.map((group) => (
                                    <React.Fragment key={group.checklistName}>
                                        <tr className="obd-src-breakdown__group">
                                            <td colSpan={4}>{group.checklistName}</td>
                                        </tr>
                                        {group.sections.map((sec) => (
                                            <tr key={`${group.checklistName}-${sec.sectionId}-${sec.sectionName}`}>
                                                <td>
                                                    <span className="obd-src-breakdown__name">{sec.sectionName}</span>
                                                    {sec.isCritical && <span className="obd-src-tag obd-src-tag--critical">Critical</span>}
                                                    {sec.isNonCritical && (
                                                        <span className="obd-src-tag obd-src-tag--noncritical">Non-Critical</span>
                                                    )}
                                                </td>
                                                <td className={sec.criticalCount > 0 || sec.reviewCount > 0 ? 'is-danger' : ''}>
                                                    {sec.answeredCount}/{sec.totalCount}
                                                </td>
                                                <td>
                                                    {sec.criticalCount > 0 || sec.reviewCount > 0 ? (
                                                        <>
                                                            {sec.criticalCount > 0 && <span className="is-danger">{sec.criticalCount}</span>}
                                                            {sec.criticalCount > 0 && sec.reviewCount > 0 && ' / '}
                                                            {sec.reviewCount > 0 && <span className="is-warning">{sec.reviewCount}</span>}
                                                        </>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td>
                                                    {sec.criticalCount > 0 && (
                                                        <span className="obd-src-status-chip is-flagged">{sec.criticalCount} flagged</span>
                                                    )}
                                                    {sec.reviewCount > 0 && (
                                                        <span className="obd-src-status-chip is-review">{sec.reviewCount} to review</span>
                                                    )}
                                                    {sec.criticalCount === 0 && sec.reviewCount === 0 && sec.status === 'complete' && (
                                                        <span className="obd-src-status-chip is-complete">
                                                            <Check size={12} /> Complete
                                                        </span>
                                                    )}
                                                    {sec.criticalCount === 0 && sec.reviewCount === 0 && sec.status === 'in-progress' && (
                                                        <span className="obd-src-status-chip is-progress">
                                                            {sec.answeredCount}/{sec.totalCount} answered
                                                        </span>
                                                    )}
                                                    {sec.criticalCount === 0 && sec.reviewCount === 0 && sec.status === 'not-started' && (
                                                        <span className="obd-src-status-chip is-progress">Not Started</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {hasAuditTrailData && (
                <div className="obd-src-summary-card">
                    <button type="button" className="obd-src-summary-card__head" onClick={() => setAuditTrailExpanded((v) => !v)}>
                        <span className="obd-src-summary-card__title">
                            <ClockCounterClockwise size={18} weight="duotone" />
                            Audit Trail
                        </span>
                        <span className="obd-src-summary-card__meta">
                            {auditTrail.totalEntriesCount} entries {auditTrail.uniqueQuestionsCount} questions
                            {auditTrailExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                        </span>
                    </button>
                    {auditTrailExpanded && (
                        <div className="obd-src-summary-card__body">
                            <div className="obd-src-sublabel">CONTRIBUTORS</div>
                            {(auditTrail.contributors || []).map((contributor) => (
                                <div key={contributor.name} className="obd-src-contributor">
                                    <div className="obd-src-contributor__who">
                                        <span className="obd-src-avatar">{contributor.initials}</span>
                                        <span>{contributor.name}</span>
                                    </div>
                                    <span className="obd-src-muted">{contributor.entriesCount} entries</span>
                                </div>
                            ))}
                            {auditTrail.contributors.length === 0 && (
                                <div className="obd-src-muted">No contributors recorded yet.</div>
                            )}

                            <div className="obd-src-filter">
                                <span className="obd-src-sublabel">SHOW:</span>
                                <button
                                    type="button"
                                    className={`obd-src-filter-btn ${auditFilter === 'changes' ? 'is-active' : ''}`}
                                    onClick={() => setAuditFilter('changes')}
                                >
                                    Changes only
                                </button>
                                <button
                                    type="button"
                                    className={`obd-src-filter-btn ${auditFilter === 'all' ? 'is-active' : ''}`}
                                    onClick={() => setAuditFilter('all')}
                                >
                                    All entries
                                </button>
                            </div>

                            {filteredAuditGroups.map((group) => (
                                <div key={group.questionId} className="obd-src-audit-group">
                                    <div className="obd-src-audit-group__head">
                                        <span>
                                            <Question size={16} /> {group.questionText}
                                        </span>
                                        <span className="obd-src-muted">
                                            {group.history.length} {group.history.length === 1 ? 'entry' : 'entries'}
                                        </span>
                                    </div>
                                    {group.history.map((h) => (
                                        <div key={h.historyId} className="obd-src-audit-row">
                                            <div className="obd-src-contributor__who">
                                                <span className="obd-src-avatar obd-src-avatar--sm">{h.modifiedUserInitials}</span>
                                                <span>{h.modifiedUserFullName}</span>
                                            </div>
                                            <div className="obd-src-audit-diff">
                                                <span className="obd-src-muted">{h.previousValue}</span>
                                                <ArrowRight size={12} />
                                                <span className={`obd-src-tone-text is-${h.answerTone}`}>{h.newValue}</span>
                                            </div>
                                            <div className="obd-src-muted obd-src-audit-time">
                                                <Clock size={12} /> {h.formattedTime}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                            {filteredAuditGroups.length === 0 && (
                                <div className="obd-src-empty-inline">No audit trail entries found matching filter.</div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
